import axios, { AxiosResponse } from 'axios';
import { captureAxiosError } from '../../instrumentation/captureApiError';
import { getClientCredentials } from '../shared/utils/cookie.utils';

// Create an Axios instance
const api = axios.create({
  baseURL: process.env.REACT_APP_API_BASE_URL, // Replace with your API's base URL
  timeout: 30000,
  maxContentLength: Infinity,
  maxBodyLength: Infinity
});

// Request interceptor to attach the Authorization header and client credentials
api.interceptors.request.use(
  config => {
    // Retrieve the access token from local storage
    const token = localStorage.getItem('accessToken');

    if (token) {
      // Attach the token to the Authorization header
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Get selected environment and attach client credentials
    const environment = (localStorage.getItem('SELECTED_ENV') || 'DEV') as 'DEV' | 'PROD';
    const credentials = getClientCredentials(environment);
    const userId = localStorage.getItem('userId');

    if (credentials) {
      // Attach client credentials as headers
      config.headers['X-Client-Id'] = credentials.clientId;
      if (credentials.clientSecret) {
        config.headers['X-Client-Secret'] = credentials.clientSecret;
        console.log(`[Axios Interceptor] ✅ Added complete client credentials for ${environment}`);
      } else {
        console.warn(
          `[Axios Interceptor] ⚠️ Added clientId but NO clientSecret for ${environment}. API may return 401.`
        );
      }
    } else {
      console.error(`[Axios Interceptor] ❌ No client credentials found for environment: ${environment}`);
      console.log('[Axios Interceptor] Available cookies:', document.cookie);
    }

    if (userId) {
      config.headers['X-User-Id'] = userId;
    }

    return config;
  },
  error => {
    // Handle the error before the request is sent
    return Promise.reject(error);
  }
);

// Add a response interceptor
api.interceptors.response.use(
  (response: AxiosResponse) => {
    if (typeof response.data === 'string') {
      try {
        // Attempt to parse stringified JSON
        response.data = JSON.parse(response.data);
      } catch (error) {
        console.warn('Response data is not valid JSON:', response.data);
      }
    }
    return response;
  },
  error => {
    captureAxiosError(error);
    return Promise.reject(error);
  }
);

export default api;
